import { memo, useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { pickPillVariant } from './pillVariant';
import { openCitationReviewWizard } from '../../hooks/openCitationReviewWizard';
import { openProvideReferenceWizard } from '../../hooks/openProvideReferenceWizard';
import { useCitationRequestForMessage } from '../../hooks/useCitationRequestForMessage';
import {
  CITATION_SEARCH_STICKY_TAIL_COUNT,
  pickCitationSearchLabels,
} from '../../model/citationSearchProgress';
import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementCitationStatus,
  StatementReferenceHelpRequest,
} from '../../model/extractedStatements';

import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import useProgressiveLoading from '@/hooks/useProgressiveLoading';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import {
  trackAddReferencesPromptViewed,
  trackProvideReferencesOpened,
} from '@/util/analyticsEvents';
import { shouldFireImpression } from '@/util/impressionDedup';

interface AddReferencesPromptBubbleProps {
  /** Twilio SID of the parent expert message. Drives the per-message
   * Zustand store registry — must match the SID used by the
   * neighbouring `ExtractedStatementsBubble` and `StatementTriageSheet`
   * so opening the pill triggers the correct sheet. */
  messageSid: string;
  /** Conversation id; emitted as `conversation_id` on the impression
   * event for analytics joins, and used to fetch the per-conversation
   * citation-request list. */
  conversationId: string;
  /** Number of extracted statements on the parent message. Rendered
   * verbatim as the badge ("{N} statements") in the resting state. */
  statementCount: number;
  /** Parsed `attachedStatementReferences` from the parent message.
   * Forwarded to `openCitationRequestReview` so the review sheet's
   * checkboxes are seeded from what's already published — opening
   * the sheet always reflects the message's current ref set. */
  attachedStatementReferences: readonly AttachedStatementReference[];
  /** Parent message's `extractedStatements` array. Forwarded into the
   * native `fullScreenModal` wizard route's handoff store so the
   * route can render the same per-statement checkbox / progress /
   * review rows the inline Actionsheet does. Unused on web (the web
   * Actionsheet/Modal stays mounted inline in the bubble). */
  extractedStatements: readonly ExtractedStatement[];
  /** Parent message's `statementCitationStatuses` array. Same purpose
   * as `extractedStatements` above. */
  statementCitationStatuses: readonly StatementCitationStatus[] | undefined;
  /** Parent message's `statementReferenceHelpRequests` array. Same
   * purpose as `extractedStatements` above. */
  statementReferenceHelpRequests?: readonly StatementReferenceHelpRequest[];
}

/**
 * Discovery affordance under the author's expert message.
 *
 * # States
 *
 * Four variants — picked by `pickPillVariant`:
 *
 * - **Resting** (no active request): `Find references · {N} statements`.
 *   Opens `StatementTriageSheet`.
 * - **Searching** (`queued | searching`): `Searching for references…`.
 *   Pill is disabled while the backend works.
 * - **Awaiting review** (`awaiting_review` | `published`):
 *   `References ready · review` with a sparkles icon. Tap opens the
 *   review sheet. `published` shares this variant on purpose — publish
 *   is a non-destructive sync, so the pill keeps advertising the
 *   review affordance after publish instead of snapping back to
 *   resting.
 * - **Failed** (`failed`): `Couldn't find references · Retry`. Tap
 *   opens the triage sheet so the expert can resend.
 *
 * `cancelled` requests produce no active request via
 * `pickActiveCitationRequest`, so this component renders the resting
 * variant in that case.
 *
 * Fires `ADD_REFERENCES_PROMPT_VIEWED` at most once per message per app
 * session via `shouldFireImpression`, so the impression is not re-counted
 * when the virtualized chat list recycles this row on scroll.
 */
export const AddReferencesPromptBubble = memo(function AddReferencesPromptBubble({
  messageSid,
  conversationId,
  statementCount,
  attachedStatementReferences,
  extractedStatements,
  statementCitationStatuses,
  statementReferenceHelpRequests,
}: AddReferencesPromptBubbleProps) {
  const { citationRequest: activeCitationRequest, isLoading: isCitationRequestLoading } =
    useCitationRequestForMessage({
      conversationId,
      messageSid,
    });

  /**
   * The in-flight "Searching…" state is shown ONLY for searches the expert
   * explicitly requested. A backend-initiated (automatic) search must not
   * distract the expert with a spinner they didn't ask for, so it stays
   * resting until it completes.
   *
   * The request itself says which it is: `requestedAt` is stamped when the
   * expert asks, including when their ask adopts an automatic search already
   * running, and is null for a search nobody asked for. Reading it off the
   * same row as `status` means the two can never disagree.
   *
   * Completion is unaffected: once the request reaches
   * `awaiting_review`/`published` it is no longer in-flight, so its found
   * references surface as "Select references" regardless of origin.
   *
   * Suppressing here rather than in the shared `useCitationRequestForMessage`
   * keeps the hook's other consumers (statements-bubble cleanup, triage
   * wizard) unaffected — and the wizard still reuses the in-flight request
   * when the resting pill is tapped.
   */
  const isInFlightSearch =
    activeCitationRequest?.status === 'queued' || activeCitationRequest?.status === 'searching';

  const suppressInFlightSearch = isInFlightSearch && !activeCitationRequest?.requestedAt;

  const citationRequest = suppressInFlightSearch ? undefined : activeCitationRequest;

  const { track } = useChatAnalytics();

  useEffect(() => {
    if (!shouldFireImpression(`add-references-prompt:${messageSid}`)) {
      return;
    }

    trackAddReferencesPromptViewed(track, {
      messageSid,
      conversationId,
      totalStatements: statementCount,
    });
  }, [messageSid, conversationId, statementCount, track]);

  const variant = pickPillVariant({
    statementCount,
    citationRequest,
  });

  const handlePress = useCallback(() => {
    if (variant.isDisabled) {
      return;
    }

    // Single entry point shared with the `referencesReady` deep-link
    // auto-open (`useAutoOpenReferencesReady`): seeds the per-request
    // review store and opens the right surface per platform — web flips
    // the inline triage store's `isOpen`; native pushes the
    // `citation-wizard` full-screen route with a populated handoff.
    openCitationReviewWizard({
      conversationId,
      messageSid,
      extractedStatements,
      statementCitationStatuses,
      attachedStatementReferences,
      statementReferenceHelpRequests,
      citationRequest,
    });
  }, [
    variant.isDisabled,
    citationRequest,
    attachedStatementReferences,
    conversationId,
    messageSid,
    extractedStatements,
    statementCitationStatuses,
    statementReferenceHelpRequests,
  ]);

  const isSearching =
    citationRequest?.status === 'queued' || citationRequest?.status === 'searching';

  /**
   * Search has returned candidates the expert can review
   * (`awaiting_review` / `published`). The card stays up in this state —
   * the left button flips to "Review Primum's references" (opens the
   * review) while "I'll add my own" remains available — so the expert is
   * never forced to choose only one path, and the copy never claims
   * "ready" next to a still-in-progress help request.
   */
  const hasFoundReferences =
    citationRequest?.status === 'awaiting_review' || citationRequest?.status === 'published';

  /**
   * The grouped two-button card replaces the single pill in the two
   * states where the expert can freely pick a flow: resting (start a
   * search or provide your own) and review-ready (review what Primum
   * found or provide your own). Searching / failed keep the single
   * status pill so their feedback isn't lost. Requires at least one
   * extracted statement to bind references to.
   */
  const showGroupedCard = statementCount > 0 && (Boolean(variant.isResting) || hasFoundReferences);

  const handleProvidePress = useCallback(() => {
    const opened = openProvideReferenceWizard({
      conversationId,
      messageSid,
      extractedStatements,
      statementCitationStatuses,
      attachedStatementReferences,
      statementReferenceHelpRequests,
    });

    if (opened) {
      trackProvideReferencesOpened(track, { conversationId, messageSid, statementCount });
    }
  }, [
    conversationId,
    messageSid,
    statementCount,
    track,
    extractedStatements,
    statementCitationStatuses,
    attachedStatementReferences,
    statementReferenceHelpRequests,
  ]);

  const [progressLabels] = useState(pickCitationSearchLabels);

  const { message: progressMessage } = useProgressiveLoading(isSearching, {
    messages: progressLabels,
    delayMs: 0,
    stickyTailCount: CITATION_SEARCH_STICKY_TAIL_COUNT,
  });

  if (isCitationRequestLoading) {
    // Empty view that holds the button's approximate height so layout
    // doesn't shift when the real button renders. No text content here
    // avoids a native-layer flash: RN applies styles asynchronously, so
    // an opacity-0 element with text can briefly appear before the style
    // instruction crosses the bridge.
    return <View className="mt-1 h-9 self-end" aria-hidden />;
  }

  if (showGroupedCard) {
    return (
      <VStack className="mt-1 items-end self-end" space="xs">
        <VStack
          className="rounded-xl border border-outline-200 bg-background-0 p-3 shadow-soft-1"
          space="sm">
          <HStack className="items-center gap-1.5">
            <Icon as={variant.icon} size="sm" className="text-primary-700" />
            <Text size="sm" className="font-semibold text-typography-900">
              Add references
            </Text>
          </HStack>
          {/* Side-by-side row. The visible primary label is kept short
              ("Review references") so the pair fits on one line at the
              card's width; flex-wrap is only a safety net for larger text
              scales. The full intent stays in the accessibility label. */}
          <HStack className="flex-wrap gap-2">
            <Button
              onPress={handlePress}
              action="primary"
              variant="solid"
              size="sm"
              hitSlop={getAccessibleHitSlop(36)}
              {...getAccessibilityProps({
                role: 'button',
                label: hasFoundReferences
                  ? 'Select from the references Primum found for these statements'
                  : 'Have Primum find references for these statements',
              })}>
              <ButtonText>
                {hasFoundReferences ? 'Select references' : 'Let Primum find them'}
              </ButtonText>
            </Button>
            <Button
              onPress={handleProvidePress}
              action="secondary"
              variant="outline"
              size="sm"
              className="bg-background-0"
              hitSlop={getAccessibleHitSlop(36)}
              {...getAccessibilityProps({
                role: 'button',
                label: 'I have references in mind',
                hint: 'Add your own references for these statements',
              })}>
              <ButtonText>I'll add my own</ButtonText>
            </Button>
          </HStack>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack className="mt-1 items-end self-end" space="xs">
      <Button
        onPress={handlePress}
        action="primary"
        variant="outline"
        size="sm"
        className="bg-background-0"
        isDisabled={variant.isDisabled}
        hitSlop={getAccessibleHitSlop(36)}
        accessibilityState={{ disabled: variant.isDisabled }}
        {...getAccessibilityProps({ role: 'button', label: variant.accessibilityLabel })}>
        {variant.useSpinner ? (
          <Spinner size="small" />
        ) : (
          <ButtonIcon as={variant.icon} className="text-primary-700" />
        )}
        <ButtonText className="text-primary-700">{variant.verb}</ButtonText>
        {variant.badge ? (
          <HStack
            className={twJoin(
              'ml-1 items-center rounded-full px-2 py-0.5',
              variant.isError ? 'bg-error-600' : 'bg-primary-700'
            )}>
            <Text size="xs" className="font-semibold text-typography-0">
              {variant.badge}
            </Text>
          </HStack>
        ) : null}
      </Button>
      {isSearching && progressMessage ? (
        <Text size="xs" className="px-1 italic text-typography-500">
          {progressMessage}
        </Text>
      ) : null}
    </VStack>
  );
});
